/**
 * The two screens that are not a turn: the quarter end, and the end of the game.
 *
 * The quarter-end panel says what the people are demanding and what missing it
 * costs, and what each bonus tile would be worth to you if you took it. The
 * scoring table is the engine's own `finalScores` — the client could not work it
 * out for anyone else, because by then the totals are folded into `vp` and
 * another seat's hand was never visible.
 */
import { MANIFESTS, type PlayerId, type View } from '@trajan/engine';

const ICONS: Record<string, string> = { bread: 'bread', games: 'games', religion: 'religion' };

/**
 * What the demand being answered is, and what the quarter's misses cost — one
 * line, for the prompt bar, while the demand itself glows on the board.
 */
export function DemandStanding({ view, seat }: { view: View; seat: PlayerId }) {
  if (view.phase.kind !== 'quarterEnd.demands') return null;
  const index = view.phase.demandIndex;
  const icon = view.demands[index]?.icon;
  const penalties = MANIFESTS.scoring.demandPenaltyByMisses;
  const missed = view.quarterEnd?.misses?.[String(seat)] ?? 0;

  return (
    <span className="end-standing">
      Demand {index + 1} of {view.demands.length}
      {icon ? `: ${ICONS[icon] ?? icon}` : ''}. Missing one costs {Math.abs(penalties[1] ?? 0)}, two{' '}
      {Math.abs(penalties[2] ?? 0)}, all three {Math.abs(penalties[3] ?? 0)} — you have missed{' '}
      {missed > 0 ? missed : 'none'} so far this quarter.
    </span>
  );
}

const LINES: { key: keyof ScoreLine; label: string }[] = [
  { key: 'duringPlay', label: 'scored in play' },
  { key: 'cardsInHand', label: 'cards in hand' },
  { key: 'workersInCamp', label: 'workers in camp' },
  { key: 'legionnairesInCamp', label: 'legionnaires in camp' },
  { key: 'trajanTilesOnMat', label: 'tiles left on the mat' },
  { key: 'constructionSets', label: 'construction sets' },
  { key: 'bonusTiles', label: 'bonus tiles' },
];

interface ScoreLine {
  duringPlay: number;
  cardsInHand: number;
  workersInCamp: number;
  legionnairesInCamp: number;
  trajanTilesOnMat: number;
  constructionSets: number;
  bonusTiles: number;
}

/** The final table: every seat, what each line of the scoring gave them, and the winner. */
export function ScoringTable({
  view,
  nameOf,
}: {
  view: View;
  nameOf?: ((seat: PlayerId) => string) | undefined;
}) {
  const scores = view.finalScores;
  const order = [...view.players].sort((a, b) => b.vp - a.vp || b.senate - a.senate);
  const winners = new Set(view.winner ?? []);

  return (
    <div className="scoring">
      <h3>
        {winners.size > 1 ? 'Winners' : 'Winner'}:{' '}
        {[...winners].map((w) => nameOf?.(w) ?? `seat ${w}`).join(', ')}
      </h3>
      {!scores && <p className="panel-elsewhere">Final scores only, without the breakdown.</p>}
      <table className="scoring-table">
        <thead>
          <tr>
            <th scope="col">&nbsp;</th>
            {order.map((p) => (
              <th key={p.id} scope="col" className={winners.has(p.id) ? 'scoring-win' : ''}>
                {nameOf?.(p.id) ?? `seat ${p.id}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scores &&
            LINES.map(({ key, label }) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                {order.map((p) => {
                  const b = scores[String(p.id)];
                  const value =
                    key === 'duringPlay'
                      ? p.vp - (b?.total ?? 0)
                      : (b?.[key as keyof typeof b] ?? 0);
                  return <td key={p.id}>{value}</td>;
                })}
              </tr>
            ))}
          <tr className="scoring-total">
            <th scope="row">total</th>
            {order.map((p) => (
              <td key={p.id} className={winners.has(p.id) ? 'scoring-win' : ''}>
                {p.vp}
              </td>
            ))}
          </tr>
          <tr className="scoring-tiebreak">
            <th scope="row">senate track</th>
            {order.map((p) => (
              <td key={p.id}>{p.senate}</td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className="panel-elsewhere">A tie on points is broken by the senate track.</p>
    </div>
  );
}
